<template>
  <div class="py-4 pb-[40px]">
    <!-- Property Address -->
    <div class="flex flex-col justify-between items-start mb-6 px-4">
      <div class="flex justify-between w-full">
        <h2 class="font-semibold">Property Address</h2>
        <div @click="goToEditProject" class="flex items-center mr-1">
          <button class="text-prime-golden text-lg">
            <img
              src="@/assets/images/edit-pen.svg"
              class="mr-1"
              alt="Edit"
              width="11"
              height="11"
            />
          </button>
          <span class="text-prime-golden font-semibold underline text-xs">edit</span>
        </div>
      </div>
      <div class="flex flex-col items-start text-xs mt-2">
        <p>{{ project?.address_1 }}</p>
        <p v-if="project?.address_2">{{ project.address_2 }}</p>
        <p>{{ project?.city }}</p>
        <p>{{ project?.state }} {{ project?.zip }}</p>
        <a
          :href="mapsLink"
          class="text-prime-golden underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Maps
        </a>
      </div>
    </div>

    <!-- Scope -->
    <div class="mb-6 flex flex-col items-start gap-2 px-4">
      <h2 class="font-semibold">Scope</h2>
      <p class="text-xs text-left">{{ project?.scope }}</p>
    </div>

    <!-- Start Date -->
    <div class="mb-6 flex flex-col items-start gap-2 px-4">
      <h2 class="font-semibold">Start Date</h2>
      <p class="text-xs">{{ formatDateToMMDDYYYY(project?.start_date) }}</p>
    </div>

    <!-- Project Status -->
    <div class="mb-6 flex flex-col items-start gap-2 px-4">
      <h2 class="font-semibold">Project Status</h2>
      <div class="grid grid-cols-[auto_auto] gap-x-8 gap-y-2">
        <div class="text-left">
          <p class="text-xs font-medium">Next Milestone:</p>
          <p class="text-xs mt-2 font-medium">Target Date:</p>
        </div>
        <div class="text-left">
          <p class="text-xs">{{ project?.next_milestone || "No milestones yet" }}</p>
          <p class="text-xs mt-2">
            {{ formatDateToMMDDYYYY(project?.next_milestone_target_date) || "N/A" }}
          </p>
        </div>
      </div>
    </div>

    <!-- Next Steps -->
    <div class="mb-6 flex flex-col items-start gap-2 px-4">
      <h2 class="font-semibold">Next Steps:</h2>
      <div class="grid grid-cols-[auto_auto] gap-x-8 gap-y-2 text-xs">
        <!-- Next Action Item -->
        <div class="font-medium text-left">Next Action Item:</div>
        <div class="text-left">
          {{ project?.next_action_item || "No action items yet" }}
        </div>

        <!-- Due Date -->
        <div class="font-medium text-left">Due Date:</div>
        <div class="text-left">
          {{ formatDateToMMDDYYYY(project?.next_action_item_due_date) || "N/A" }}
        </div>

        <!-- Owner -->
        <div class="font-medium text-left">Owner(s):</div>
        <div class="text-left">
          <template v-if="actionItemOwners?.length > 0">
            <div
              v-for="(owner, idx) in actionItemOwners"
              :key="owner.id || idx"
              class="flex items-center mb-1"
            >
              <p class="mr-2">
                <a
                  v-if="owner.id"
                  @click.prevent="viewContact(owner.id)"
                  class="text-prime-golden underline cursor-pointer"
                >
                  {{ owner.first_name || "N/A" }} {{ owner.last_name || "" }}
                  <!-- Link to open contact page if ID is present -->
                </a>
              </p>
            </div>
          </template>
          <template v-else>
            <span class="">N/A</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Project Manager -->
    <div class="mb-6 flex flex-col items-start gap-3 mt-10">
      <h2 class="font-semibold px-4">Project Manager</h2>
      <ContactCard
        :avatar="projectManagerAvatar"
        :name="projectManagerName"
        :role="project?.project_manager?.role || 'Project Manager'"
        :phoneNumber="project?.project_manager?.phone || 'N/A'"
        @optionsClicked="openContactDrawer('projectManager')"
        @click="viewContact(project?.project_manager?.id)"
      />
    </div>

    <!-- Homeowner -->
    <div v-if="project?.homeowner" class="mb-6 flex flex-col items-start gap-3 mt-10">
      <h2 class="font-semibold px-4">Homeowner</h2>
      <ContactCard
        :avatar="homeownerAvatar"
        :name="homeownerName"
        :role="project?.homeowner?.role || 'Homeowner'"
        :phoneNumber="project?.homeowner?.phone || 'N/A'"
        @optionsClicked="openContactDrawer('homeowner')"
        @click="viewContact(project?.homeowner?.id)"
      />
    </div>

    <!-- Contact Drawer -->
    <ContactDrawer
      :isOpen="isContactDrawerOpen"
      :contact="selectedContact"
      @close="closeContactDrawer"
      :currentTab="'overview'"
      :projectId="project?.id || ''"
    />
  </div>
</template>

<script>
import ContactCard from "./ContactCard.vue";
import { useProjectStore } from "@/stores/project";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import ContactDrawer from "./ContactDrawer.vue";

export default {
  name: "OverviewView",
  components: {
    ContactCard,
    ContactDrawer,
  },
  setup() {
    const router = useRouter();
    const projectStore = useProjectStore();
    const project = projectStore?.currentProject;


    const formatDateToMMDDYYYY = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        weekday: "short",
        day: "numeric",
      };
      return date.toLocaleString(undefined, options);
    };

    const goToEditProject = () => {
      router.push({ name: "EditProject", params: { id: project?.id } });
    };

    const isContactDrawerOpen = ref(false);
    const selectedContact = ref(null);

    const openContactDrawer = (contactType) => {
      if (contactType === "projectManager") {
        selectedContact.value = {
          id: project?.project_manager?.id || 1,
          first_name: project?.project_manager?.first_name || "Random",
          last_name: project?.project_manager?.last_name || "Person",
          email: project?.project_manager?.email || "projectmanager@example.com",
          phone: project?.project_manager?.phone || "123-456-7890",
          role: project?.project_manager?.role || "Senior Project Manager",
        };
      } else if (contactType === "homeowner") {
        selectedContact.value = {
          id: project?.homeowner?.id || 2,
          first_name: project?.homeowner?.first_name || "Random",
          last_name: project?.homeowner?.last_name || "Person",
          email: project?.homeowner?.email || "homeowner@example.com",
          phone: project?.homeowner?.phone || "098-765-4321",
          role: project?.homeowner?.role || "Homeowner",
        };
      }
      isContactDrawerOpen.value = true;
    };

    const closeContactDrawer = () => {
      isContactDrawerOpen.value = false;
      selectedContact.value = null;
    };

    const viewContact = (contactId) => {
      router.push({
        name: "ContactViewPage",
        params: { id: contactId },
        query: { projectId: project?.id, tab: "overview" },
      });
    };

    const mapsLink = computed(() => {
      if (project) {
        const addressParts = [
          project.address_1,
          project.address_2,
          project.city,
          project.state,
          project.zip,
          project.country,
        ].filter(Boolean);

        const addressString = addressParts.join(", ");
        const encodedAddress = encodeURIComponent(addressString);
        return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      }
      return "#";
    });

    // Computed properties for dynamic data with fallbacks
    const projectManagerName = computed(() =>
      project?.project_manager
        ? `${project.project_manager.first_name} ${project.project_manager.last_name}`
        : "Random Person"
    );

    const actionItemOwners = computed(() => {
      return project?.action_item_owners || [];
    });

    const homeownerName = computed(() =>
      project?.homeowner
        ? `${project.homeowner.first_name} ${project.homeowner.last_name}`
        : "Random Person"
    );

    const projectManagerAvatar = computed(
      () => project?.project_manager?.profile_image_url
    );

    const homeownerAvatar = computed(() => project?.homeowner?.profile_image_url);

    return {
      project,
      goToEditProject,
      isContactDrawerOpen,
      selectedContact,
      openContactDrawer,
      closeContactDrawer,
      mapsLink,
      formatDateToMMDDYYYY,
      projectManagerName,
      homeownerName,
      projectManagerAvatar,
      homeownerAvatar,
      viewContact,
      actionItemOwners,
    };
  },
};
</script>

<style scoped></style>
